import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';

const cmsLogos = [
  { name: 'Webflow', src: '/assets/webflow.png' },
  { name: 'WordPress', src: '/assets/wordpress.png' },
  { name: 'Shopify', src: '/assets/shopify.png' },
  { name: 'Framer', src: '/assets/framer.png' },
  { name: 'React', src: '/assets/react.png' },
  { name: 'Next.js', src: '/assets/next.png' }
];

const integrations = [
  {
    id: 'webflow',
    name: 'Webflow',
    category: 'No-code CMS',
    summary:
      'We connect to Webflow via its CMS API and publish GEO/AIO-optimized pages, collections, and component updates without breaking your visual design system.',
    details: [
      'Use Webflow CMS collections for programmatic GEO/AIO landing pages, comparison pages, and FAQ clusters.',
      'Publish content changes through the Webflow API so your marketing team can still work visually in the designer.',
      'Inject structured data and metadata (schema, canonical tags, Open Graph) through custom fields and template logic.',
      'Track AI-influenced sessions by wiring Webflow forms and events into your analytics + GEO/AIO reporting stack.'
    ]
  },
  {
    id: 'wordpress',
    name: 'WordPress',
    category: 'CMS',
    summary:
      'We integrate with WordPress using REST or WPGraphQL and deploy GEO/AIO content via custom post types, taxonomies, and templates.',
    details: [
      'Create or extend custom post types for AI-focused content hubs, programmatic location pages, or product/category templates.',
      'Use ACF or similar plugins for structured fields that map cleanly into schema and AI-friendly markup.',
      'Automate publishing workflows via the REST API or WP-CLI, while preserving your existing theme and SEO plugins.',
      'Wire WordPress forms and events into your CRM and GEO/AIO dashboards for full-funnel tracking.'
    ]
  },
  {
    id: 'shopify',
    name: 'Shopify',
    category: 'Ecommerce',
    summary:
      'For Shopify, we extend your theme and product catalog with AI-optimized collection pages, PDP enhancements, and internal linking structures.',
    details: [
      'Generate GEO/AIO-ready landing pages for key categories, bundles, and use cases using metafields and dynamic sections.',
      'Enhance product detail pages with AI-readable specs, FAQs, and comparison blocks without hurting performance.',
      'Use Shopify APIs and webhooks to sync inventory, pricing, and content changes into your GEO/AIO automation flows.',
      'Integrate with your existing analytics and attribution setup to measure AI-influenced revenue, not just clicks.'
    ]
  },
  {
    id: 'framer',
    name: 'Framer',
    category: 'Visual builder',
    summary:
      'With Framer, we strike the balance between design control and structured content needed for GEO/AIO.',
    details: [
      'Define reusable Framer components that accept structured props for AI-focused content blocks and layouts.',
      'Connect Framer to a headless CMS or data source so we can update copy and sections without manual edits.',
      'Implement lightweight routing and internal linking patterns that help AI engines understand your information architecture.',
      'Layer in tracking and event instrumentation so GEO/AIO experiments are testable inside Framer projects.'
    ]
  },
  {
    id: 'react',
    name: 'React',
    category: 'Frontend framework',
    summary:
      'For React apps, we work at the component and routing level to make sure AI engines can interpret your UI as structured, crawlable content.',
    details: [
      'Audit your routing, metadata, and rendering mode (CSR/SSR/SSG) to ensure AI engines can reliably access key pages.',
      'Introduce layout and content components that separate presentation from structured data and copy.',
      'Integrate with your data layer (REST/GraphQL) to generate GEO/AIO pages around entities, locations, and use cases.',
      'Add analytics hooks and events that distinguish AI-influenced sessions from other acquisition channels.'
    ]
  },
  {
    id: 'nextjs',
    name: 'Next.js',
    category: 'React meta-framework',
    summary:
      'Next.js is ideal for GEO/AIO because we can combine file-based routing, dynamic segments, and static generation for fast, AI-friendly pages.',
    details: [
      'Use dynamic routes and static generation (SSG/ISR) to ship thousands of GEO/AIO pages with strong performance.',
      'Attach structured data via `next/head` or metadata APIs so AI engines see clean, machine-readable context.',
      'Integrate server-side data fetching (or RSCs in newer Next.js) with your CMS, product catalog, or internal APIs.',
      'Configure edge caching and revalidation strategies that keep AI-facing content fresh without constant rebuilds.'
    ]
  },
  {
    id: 'astro',
    name: 'Astro',
    category: 'Content-first framework',
    summary:
      'With Astro, we lean into its content-focused architecture and islands model to build highly-performant, AI-readable sites.',
    details: [
      'Use Astro content collections and MDX for structured, GEO/AIO-optimized editorial hubs and resource libraries.',
      'Render most content as static HTML for maximum crawlability, layering interactivity only where it materially helps UX.',
      'Integrate Astro with headless CMSes or data sources to power programmatic pages and AI-focused content patterns.',
      'Wire analytics and event tracking at the layout level so AI-driven visits are measurable without heavy client bundles.'
    ]
  },
  {
    id: 'vue-nuxt',
    name: 'Vue.js / Nuxt',
    category: 'Vue ecosystem',
    summary:
      'For Vue and Nuxt, we align your component architecture and routing strategy with GEO/AIO and AI-crawlability best practices.',
    details: [
      'In Nuxt, use file-based routing and static or hybrid rendering modes to pre-render key GEO/AIO landing pages.',
      'Map your Vue components to clear content blocks so we can attach schema, headings, and internal links consistently.',
      'Connect to your CMS or backend via Nuxt server routes or composables for dynamic programmatic pages.',
      'Instrument AI-influenced journeys with composable-based analytics and event tracking.'
    ]
  },
  {
    id: 'svelte',
    name: 'Svelte / SvelteKit',
    category: 'Svelte ecosystem',
    summary:
      'In Svelte and SvelteKit projects, we use the routing and load APIs to generate fast, AI-optimized content structures.',
    details: [
      'Use SvelteKit routes and `load` functions to fetch and pre-render GEO/AIO content entities as static or hybrid pages.',
      'Keep content-heavy sections server-rendered, with islands of interactivity where it supports conversions and UX.',
      'Integrate SvelteKit with your CMS or data layer for programmatic pages around products, locations, or use cases.',
      'Implement lightweight analytics and event tracking so AI-visible pages are measurable without heavy client overhead.'
    ]
  }
];

export const Integrations = () => {
  const [activeId, setActiveId] = useState(integrations[0].id);
  const activeIntegration = integrations.find((i) => i.id === activeId) ?? integrations[0];

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <section className="pt-24 lg:pt-32 pb-12 lg:pb-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold mb-6">
            Integrations That Fit Your Stack
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            Evolution GEO plugs into the tools you already use—so GEO/AIO, AI ads, and automations work with your CMS, analytics, and communication stack instead of replacing them.
          </p>
        </div>
      </section>

      {/* Sidebar-powered integration details */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[260px,1fr] gap-10 lg:gap-16">
            {/* Sticky sidebar */}
            <aside className="sticky top-20 self-start">
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Integrations
                </h2>
              </div>
              <div className="space-y-1 rounded-xl border border-border bg-card/40 p-2">
                {integrations.map((integration) => {
                  const isActive = integration.id === activeId;
                  return (
                    <button
                      key={integration.id}
                      type="button"
                      onClick={() => setActiveId(integration.id)}
                      className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:bg-muted'
                      }`}
                    >
                      <div className="font-medium">{integration.name}</div>
                      <div className="text-[11px] opacity-80">{integration.category}</div>
                    </button>
                  );
                })}
              </div>
            </aside>

            {/* Detail canvas */}
            <div>
              <Card className="border-border shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="font-display text-2xl">
                    {activeIntegration.name} Integration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Image placeholder */}
                  <div className="h-[90px] rounded-lg border border-dashed border-border bg-muted/40 flex items-center justify-center text-[11px] text-muted-foreground mb-2">
                    {activeIntegration.name} integration visual (provided later)
                  </div>

                  <p className="text-sm text-foreground">
                    {activeIntegration.summary}
                  </p>

                  <div className="space-y-2">
                    {activeIntegration.details.map((item, index) => (
                      <div key={index} className="flex items-start space-x-2">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                        <p className="text-sm text-muted-foreground">{item}</p>
                      </div>
                    ))}
                  </div>

                  <div className="pt-4">
                    <Button
                      size="sm"
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                      asChild
                    >
                      <a href="/schedule-call">
                        Schedule a Call about {activeIntegration.name}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Coming soon */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-dashed border-border">
            <CardHeader>
              <CardTitle className="font-display text-2xl">Other Integrations</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                We also integrate with CRMs, analytics tools, data warehouses, and workflow platforms. As we scope your project, we&apos;ll map out which connections are required for GEO/AIO tracking, reporting, and automations.
              </p>
              <p className="text-muted-foreground">
                If you have a specific tool in mind, we&apos;ll confirm support during onboarding and include integration work in your roadmap.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-6">
            Curious If We Support Your Stack?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Share your CMS and main tools, and we&apos;ll confirm integration options as part of your free GEO/AIO audit.
          </p>
          <Button asChild size="lg" className="gradient-primary text-primary-foreground shadow-primary">
            <a href="/free-audit">
              Get My Free Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

